import { CommonModule, DatePipe, DecimalPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
} from '@angular/forms';
import { ConfirmationService, SortEvent } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ChipModule } from 'primeng/chip';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { Paginator, PaginatorModule } from 'primeng/paginator';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { OrderTypeEnums } from 'src/app/enum/order-enums';
import { RemissionOrderStateEnum } from 'src/app/enum/remission-order-state-enum';
import {
  ActionColor,
  CheckBoxAction,
  TableAction,
} from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { environment } from 'src/environments/environment';
import { ModalObjectType } from 'src/app/interfaces/modal-object-types.interface';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { ColumnResponseModel } from '../../models/responses/column-response.model';
import { PagedResponseModel } from '../../models/responses/paged-response.model';
import { CustomButtonComponent } from '../custom-components/custom-button/custom-button.component';
import { CustomFiltersComponent } from '../custom-filters/custom-filters/custom-filters.component';
import { TransportInvoiceEnum } from 'src/app/enum/transport-invoice-enum';
import { Image } from 'primeng/image';

@Component({
  selector: 'app-custom-table',
  templateUrl: './custom-table.component.html',
  styleUrls: ['./custom-table.component.scss'],
  standalone: true,
  imports: [
    CustomFiltersComponent,
    ChipModule,
    PaginatorModule,
    FormsModule,
    ButtonModule,
    TableModule,
    CommonModule,
    RadioButtonModule,
    DropdownModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    ReactiveFormsModule,
    CheckboxModule,
    CustomButtonComponent,
    Image,
    TooltipModule
  ],
  providers: [ConfirmationService],
})
export class CustomTableComponent implements OnInit, AfterViewInit {
  @Input() headers: ColumnResponseModel[] = [];
  @Input() showCheckbox: boolean = false;
  @Input() isMultipleSelect: boolean = true;
  @Input() showButtonAdd: boolean = true;
  @Input() data: PagedResponseModel<unknown> =
    new PagedResponseModel<unknown>();
  @Input() dataKey: string = 'id';
  @Input() actions: TableAction[] = [];
  @Input() checkBoxAction: CheckBoxAction[] = [];
  @Input() sortableFields: string[] = [];
  @Input() listStoreConsolidated: ColumnResponseModel =
    new ColumnResponseModel();
  @Input() listDifferences: ColumnResponseModel =
    new ColumnResponseModel();
  @Input() listRemissionOrderState: ColumnResponseModel =
    new ColumnResponseModel();
  @Input() listOrderType: ColumnResponseModel =
    new ColumnResponseModel();
  @Input() listShowView: ColumnResponseModel =
    new ColumnResponseModel();
  @Input() limitHidden: boolean = false;
  @Input() limitPaginator: boolean = false;
  @Input() storeConsolidated: boolean = true;
  @Input() differences: number = TransportInvoiceEnum.allDocuments;
  @Input() initialSelectionId: number | string | null = null;
  @Input() showDeployBy: boolean = true;
  @Input() remissionOrderState: number = RemissionOrderStateEnum.pending;
  @Input() orderTypeId: number = OrderTypeEnums.rental;
  @Input() previousFilters: any[] = [];
  @Input() selectAllByDefault: boolean = false;
  @Input() showExport: boolean = false;
  @Input() showImport: boolean = false;
  @Input() showRecover: boolean = false;
  @Input() isFilterPersistent: boolean = true;
  @Input() filterPersistentKey: string = '';
  @Input() radioButton: boolean = false;
  @Input() disableDoubleClick: boolean = false;
  @Input() clearSelectionTrigger: number = 0;
  @Input() rowClass: (item: any) => string = () => '';

  @Output() onPageChange = new EventEmitter<{
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }>();
  @Output() onAction = new EventEmitter<{
    action: string;
    item?: any;
    selectedItems?: number[];
  }>();
  @Output() onFilterChange = new EventEmitter<{ filters: any }>();
  @Output() onExport = new EventEmitter();
  @Output() onImport = new EventEmitter();
  @Output() onRecoverData = new EventEmitter();
  @Output() onReload = new EventEmitter<void>();
  @Output() onBulkDelete = new EventEmitter();
  @Output() storeConsolidatedEmit = new EventEmitter<boolean>();
  @Output() differencesEmit = new EventEmitter<number>();
  @Output() remissionOrderStateEmit = new EventEmitter<number>();
  @Output() orderTypeEmit = new EventEmitter<number>();
  @Output() showDeployByEmit = new EventEmitter<boolean>();
  @ViewChild('paginator') paginator!: Paginator;
  @ViewChild('dt') dt: Table;
  @Output() selectionChange = new EventEmitter<any>();

  public readonly MAX_FROZEN_COLUMNS = 3;

  private originalColumnPositions: Map<string, number> = new Map<
    string,
    number
  >();
  private frozenColumns: string[] = [];
  private _filtersApplied = false;

  visualSelectedItem: any = null;
  selectedItems: any[] = [];
  statuses!: any[];
  actionFrozen: boolean = true;
  classPaginator: string = '';
  innerHeaders: ColumnResponseModel[] = [];
  previousSort: { field: string | undefined; isAscsendant: number | undefined };
  filteredFilters: any[] = [];
  paginatorArray: any[] = [5, 10, 20, 50, 100, 200, 500];
  previousPageSize = 10;
  selectedValue: boolean = true;
  formSelect: UntypedFormGroup = new UntypedFormGroup({});
  formFilters: UntypedFormGroup = new UntypedFormGroup({});
  filtersChanged: boolean = false;
  first: number = 0;
  isSelectedItems: boolean = false;
  selectedRadioItem: any = null;

  get countSelectedItems(): number {
    return this.selectedItems?.length || 0;
  }

  isResizing: boolean = false;

  private imageBlobCache: Map<string, SafeUrl | null> = new Map();
  private loadingImages: Set<string> = new Set();

  constructor(
    private confirmationService: ConfirmationService,
    @Optional() private ref: DynamicDialogRef,
    private dateConverterService: DateConverterService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef,
    private decimalPipe: DecimalPipe,
    private formBuilder: FormBuilder,
    private httpClient: HttpClient,
    private sanitizer: DomSanitizer
  ) {
    this.formSelect = this.formBuilder.group({
      select: null,
    });
    this.formFilters = this.formBuilder.group({
      filters: null,
    });
  }

  ngOnInit() {
    this.data.pageSize = Number(this.data.pageSize) || 10;
    this.initializeFilters();
  }

  initializeFilters() {
    this.formFilters = this.formBuilder.group({});
    this.headers.forEach(header => {
      if (header.isFilter) {
        this.formFilters.addControl(header.propertyName, new FormControl(null));
      }
    });
  }

  toggleRadio(item: any) {
    this.selectedRadioItem = item;
    this.selectionChange.emit({ selectedItem: item });
  }

  // function to emit selected data
  returnSelectedData() {
    if (this.showButtonAdd) {
      const data: ModalObjectType = {};
      data.selectedItems = this.selectedItems;
      if (this.ref) this.ref.close(data);
    } else {
      this.selectionChange.emit({ selectedItem: this.selectedItems });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isMultipleSelect']) {
      setTimeout(() => this.cdr.detectChanges(), 0);
    }
    if (changes['limitHidden']) {
      this.classPaginator = this.limitHidden ? 'limit-hidden' : '';
    }
    if (changes['limitPaginator']) {
      //Limitamos paginador para selecciones basicas con imagenes
      this.paginatorArray = this.limitPaginator ? [5, 10, 50, 100, 200] : [10, 20, 50, 100, 200, 500];
    }
    this.formFilters.reset();
    this.handleAddRegColumn();
    const headers = changes['headers'];
    const data = changes['data'];

    this.onReportTypeChanges(changes);

    if (data) {
      this.updateTableView();
      this.data.items = data.currentValue.items ?? [];

      const isEqual = (a: any, b: any) =>
        (this.dataKey && a[this.dataKey] !== undefined && b[this.dataKey] !== undefined)
          ? a[this.dataKey] == b[this.dataKey] : a == b;

      if (this.showCheckbox && this.isMultipleSelect && this.selectAllByDefault) {
        this.data.items.forEach(item => {
          if (!this.selectedItems.some(i => isEqual(i, item))) {
            this.selectedItems.push(item);
          }
        });
        this.isSelectedItems = this.selectedItems.length > 0;
      }

      if (this.filtersChanged) this.applyLocalStorageFilter();
      this.filtersChanged = false;

      if (data.currentValue.currentPage === 1) this.first = 0;
    }

    if (changes['clearSelectionTrigger'] && !changes['clearSelectionTrigger'].firstChange) {
      if (changes['clearSelectionTrigger'].currentValue !== changes['clearSelectionTrigger'].previousValue) {
        this.clearSelections();
      }
    }

    if (!headers?.firstChange) {
      this.buildFiltersFormGroup();
      this.sharedFilters(headers);

      this.applyPreviousFilters();
      this.applyFrozenColumns();
    }

    if ((changes['data'] ||
      changes['initialSelectionId']) && this.data?.items) {
      this.syncSelection();
    }
  }

  onReportTypeChanges(changes: SimpleChanges) {
    const orderTypeId = changes['orderTypeId'];
    const storeConsolidated = changes['storeConsolidated'];
    const differences = changes['differences'];
    const remissionOrderState = changes['remissionOrderState'];
    const showDeployBy = changes['showDeployBy'];

    if (storeConsolidated) {
      this.updateStoreConsolidated(storeConsolidated);
    }

    if (differences) {
      this.updateDifferences(differences);
    }

    if (remissionOrderState) {
      this.updateRemissionOrderState(remissionOrderState);
    }

    if (orderTypeId) {
      this.updateOrderType(orderTypeId);
    }

    if (showDeployBy) {
      this.updateShowDeployBy(showDeployBy);
    }
  }

  ngAfterViewChecked() {
    // Use flag in order to run once
    if (
      !this._filtersApplied &&
      this.formFilters &&
      Object.keys(this.formFilters.controls).length > 0
    ) {
      this._filtersApplied = true;
      this.applyPreviousFilters();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => this.applyFrozenColumns(), 0);
    this.resetPagination();

    // escucha global del mouseup para capturar fin de drag
    window.addEventListener('mouseup', this.onGlobalMouseUp.bind(this));

  }

  ngOnDestroy() {
    window.removeEventListener('mouseup', this.onGlobalMouseUp.bind(this));
  }

  private resetPagination() {
    const paginators = document.querySelectorAll('.p-paginator.p-component');

    if (paginators.length === 0) return;

    paginators.forEach((paginator) => {
      const buttons = paginator.querySelectorAll('.p-paginator-page');

      buttons.forEach((btn) => {
        btn.classList.remove('p-paginator-page-selected');
        const label = btn.getAttribute('aria-label');
        if (label === String(this.data.currentPage)) {
          btn.classList.add('p-paginator-page-selected');
        }
      });
    });
  }

  public isSelectedItem(item: any): boolean {
    if (this.selectedItems.length) return false;
    return item;
  }

  isAllSelected(): boolean {
    if (!this.data.items || this.data.items.length === 0) return false;

    const isEqual = (a: any, b: any) =>
      (this.dataKey && a[this.dataKey] !== undefined && b[this.dataKey] !== undefined)
        ? a[this.dataKey] == b[this.dataKey] : a == b;

    return this.data.items.every(item => this.selectedItems.some(sel => isEqual(sel, item)));
  }

  toggleSelectAll(checked: boolean): void {
    const isEqual = (a: any, b: any) =>
      (this.dataKey && a[this.dataKey] !== undefined && b[this.dataKey] !== undefined)
        ? a[this.dataKey] == b[this.dataKey] : a == b;

    if (checked) {
      this.data.items.forEach(item => {
        if (!this.selectedItems.some(sel => isEqual(sel, item))) {
          this.selectedItems.push(item);
        }
      });
    } else {
      this.data.items.forEach(item => {
        this.selectedItems = this.selectedItems.filter(sel => !isEqual(sel, item));
      });
    }
    this.isSelectedItems = this.selectedItems.length > 0;
    this.selectionChange.emit({ selectedItem: this.selectedItems });
  }

  handleSingleSelectionChange(item: any) {
    this.selectedItems = [item];
    this.visualSelectedItem = item;
    this.isSelectedItems = true;

    this.returnSelectedData();
  }

  handleCheckboxChange(item: any, checked: boolean): void {
    this.isRowSelectedVisually(item);
    // Función auxiliar para comparar usando dataKey si existe
    const isEqual = (a: any, b: any) =>
      (this.dataKey && a[this.dataKey] !== undefined && b[this.dataKey] !== undefined)
        ? a[this.dataKey] == b[this.dataKey] : a == b;

    if (checked) {
      if (!this.selectedItems.some(i => isEqual(i, item))) {
        this.selectedItems.push(item);
      }
    } else {
      this.selectedItems = this.selectedItems.filter(i => !isEqual(i, item));
    }
    this.isSelectedItems = this.selectedItems.length > 0;
    this.selectionChange.emit({ selectedItem: this.selectedItems });
  }

  isItemChecked(item: any): boolean {
    const isEqual = (a: any, b: any) =>
      (this.dataKey && a[this.dataKey] !== undefined && b[this.dataKey] !== undefined)
        ? a[this.dataKey] == b[this.dataKey] : a == b;
    return this.selectedItems.some((i) => isEqual(i, item));
  }

  private buildFiltersFormGroup() {
    this.headers.forEach((header: ColumnResponseModel) => {
      if (header.isFilter) {
        if (
          header.inputType === 'NumberRange' ||
          header.inputType === 'DateRange' ||
          header.inputType === 'DateTimeRange'
        ) {
          this.formFilters.addControl(
            header.propertyName,
            this.formBuilder.control(null)
          );
          this.formFilters.addControl(
            `${header.propertyName}Alter`,
            this.formBuilder.control(null)
          );
        } else {
          this.formFilters.addControl(
            header.propertyName,
            this.formBuilder.control(null)
          );
        }
      }
    });
  }

  private sharedFilters(headers: SimpleChange) {
    this.filteredFilters = headers?.firstChange
      ? this.previousFilters
      : this.previousFilters.filter((filter) =>
        this.headers.some(
          (header) => header.propertyName === filter.propertyName
        )
      );

    if (
      !this.filteredFilters.some(
        (filter) => filter.propertyName === 'deleteAll'
      ) &&
      this.filteredFilters.length > 1
    ) {
      this.filteredFilters.unshift({
        propertyName: 'Eliminar todos',
        valueFilter: 'deleteAll',
      });
    }
  }

  private syncSelection() {
    if (this.initialSelectionId && this.data.items && this.data.items.length > 0) {
      const found = this.data.items.find((item: any) => {
        return item[this.dataKey] === this.initialSelectionId;
      });

      if (found) {
        this.selectedRadioItem = found;
      }
    }
  }

  public getFormControl(propertyName: string) {
    const control = this.formFilters.get(propertyName) as FormControl;
    const existingFilter = this.previousFilters.find(
      (f) => f.propertyName === propertyName
    );

    if (existingFilter && control && control.value === null) {
      control.setValue(existingFilter.valueFilter);
      if (existingFilter.valueAlterFilter) {
        const alterControl = this.formFilters.get(
          `${propertyName}Alter`
        ) as FormControl;
        if (alterControl) {
          alterControl.setValue(existingFilter.valueAlterFilter);
        }
      }
    }

    return control;
  }

  public getFormControlAlter(field: string): FormControl {
    return this.formFilters?.get(`${field}Alter`) as FormControl;
  }

  applyLocalStorageFilter() {
    const filters = localStorage.getItem(this.filterPersistentKey);
    if (this.isFilterPersistent && filters) {
      this.previousFilters = JSON.parse(filters);
      this.applyPreviousFilters();
      this.onFilterChange.emit({ filters: this.previousFilters });
      this.recoverData();
    }
  }

  handleRowClick(item: any, event: MouseEvent) {
    const clickedElement = event.target as HTMLElement;

    // if it was a checkbox or radio, does not change the visual selection
    if (
      clickedElement.closest('p-tableCheckbox') ||
      clickedElement.closest('p-radioButton') ||
      clickedElement.tagName === 'INPUT'
    ) {
      return;
    }

    this.visualSelectedItem = item;
  }

  isRowSelectedVisually(item: any): boolean {
    return this.visualSelectedItem === item;
  }

  getRowClasses(item: any): Record<string, boolean> {
    const classes: Record<string, boolean> = {
      'selected-row': this.isRowSelectedVisually(item),
      'checked-row': this.isItemChecked(item)
    };
    const extraClass = this.rowClass(item);
    if (extraClass) classes[extraClass] = true;
    return classes;
  }

  private applyPreviousFilters() {
    if (!this.previousFilters || this.previousFilters.length === 0) return;

    this.previousFilters.forEach((filter) => {
      if (filter.propertyName === 'deleteAll') return;

      const control = this.formFilters.get(filter.propertyName);
      if (!control) {
        console.warn(`No se encontró control para ${filter.propertyName}`);
        return;
      }

      const headerInfo = this.headers.find(
        (h) => h.propertyName === filter.propertyName
      );
      const inputType = headerInfo?.inputType?.toLowerCase();

      try {
        if (inputType === 'date' || inputType === 'daterange') {
          if (filter.valueFilter) {
            let startDate = filter.valueFilter;
            let endDate = filter?.valueAlterFilter;

            if (typeof startDate === 'string') {
              const parts = startDate.split('-');
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const day = parseInt(parts[2], 10);
              startDate = new Date(
                Number(year),
                Number(month),
                Number(day),
                12,
                0,
                0
              );
            }

            if (typeof endDate === 'string') {
              const parts = endDate.split('-');
              const year = parseInt(parts[0], 10);
              const month = parseInt(parts[1], 10) - 1;
              const day = parseInt(parts[2], 10);
              endDate = new Date(
                Number(year),
                Number(month),
                Number(day),
                12,
                0,
                0
              );
            }

            if ((inputType === 'daterange') && filter.valueAlterFilter) {
              const alterControl = this.formFilters.get(
                `${filter.propertyName}Alter`
              );
              if (alterControl) {
                const dateRange = [startDate, endDate];
                control.setValue(dateRange, { emitEvent: false });
              }
            } else {
              control.setValue(startDate, { emitEvent: false });
            }
            setTimeout(() => {
              try {
                const datepicker = document.querySelector(
                  `p-datepicker[formcontrolname="${filter.propertyName}"]`
                );
                if (datepicker) {
                  const input = datepicker.querySelector('input');
                  if (input) {
                    const changeEvent = new Event('change', { bubbles: true });
                    input.dispatchEvent(changeEvent);
                  }
                }
              } catch (err) {
                console.error('Error al actualizar datepicker:', err);
              }
            }, 100);
          }
        } else if (inputType === 'datetimerange') {
          if (filter.valueFilter) {
            let startDate = filter.valueFilter;
            let endDate = filter?.valueAlterFilter;

            if (typeof startDate === 'string') {
              startDate = this.dateConverterService.convertUtcToLocalDateFull(startDate);
            }

            if (typeof endDate === 'string') {
              endDate = this.dateConverterService.convertUtcToLocalDateFull(endDate);
            }

            control.setValue(startDate, { emitEvent: false });

            if (filter.valueAlterFilter) {
              const alterControl = this.formFilters.get(
                `${filter.propertyName}Alter`
              );
              if (alterControl) {
                alterControl.setValue(endDate, { emitEvent: false });
              }
            }
          }
        } else {
          // Para otros tipos de filtros

          control.setValue(filter.valueFilter, { emitEvent: false });
          // Para NumberRange, establecer el valor alter
          if (
            inputType === 'numberrange' &&
            filter.valueAlterFilter !== undefined
          ) {
            const alterControl = this.formFilters.get(
              `${filter.propertyName}Alter`
            );
            if (alterControl) {
              alterControl.setValue(filter.valueAlterFilter, {
                emitEvent: false,
              });
            }
          }
        }
      } catch (error) {
        console.error(
          `Error al aplicar filtro para ${filter.propertyName}:`,
          error
        );
      }
    });
  }

  private updateStoreConsolidated(storeConsolidated: SimpleChange) {
    {
      if (this.storeConsolidated === undefined || this.storeConsolidated === null ||
        this.storeConsolidated === true) {
        this.formSelect.controls['select'].setValue({
          value: 'true',
          label: 'Consolidado',
        });
      } else {
        const storedLabel = this.listStoreConsolidated.itemsSelect.find(
          (x) => x.value === String(storeConsolidated.currentValue)
        );
        this.formSelect.controls['select'].setValue({
          value: String(storeConsolidated.currentValue),
          label: storedLabel?.label,
        });
      }
    }
  }

  updateDifferences(differences: SimpleChange) {
    {
      if (this.differences === undefined || this.differences === null ||
        this.differences === TransportInvoiceEnum.allDocuments) {
        this.formSelect.controls['select'].setValue({
          value: String(TransportInvoiceEnum.allDocuments),
          label: 'Todos',
        });
      } else {
        const differencesLabel = this.listDifferences.itemsSelect.find(
          (x) => x.value === String(differences.currentValue)
        );
        this.formSelect.controls['select'].setValue({
          value: String(differences.currentValue),
          label: differencesLabel?.label,
        });
      }
    }
  }

  updateRemissionOrderState(remissionOrderState: SimpleChange) {
    {
      if (this.remissionOrderState === undefined || this.remissionOrderState === null ||
        this.remissionOrderState === RemissionOrderStateEnum.pending) {
        this.formSelect.controls['select'].setValue({
          value: RemissionOrderStateEnum.pending.toString(),
          label: 'Pendientes',
        });
      } else {
        const remissionOrderStateLabel = this.listRemissionOrderState.itemsSelect.find(
          (x) => x.value === String(remissionOrderState.currentValue)
        );
        this.formSelect.controls['select'].setValue({
          value: String(remissionOrderState.currentValue),
          label: remissionOrderStateLabel?.label,
        });
      }
    }
  }

  updateOrderType(orderTypeId: SimpleChange) {
    {
      if (this.orderTypeId === undefined || this.orderTypeId === null ||
        this.orderTypeId === OrderTypeEnums.rental) {
        this.formSelect.controls['select'].setValue({
          value: OrderTypeEnums.rental.toString(),
          label: 'Alquiler',
        });
      } else {
        const orderTypeLabel = this.listOrderType.itemsSelect.find(
          (x) => x.value === String(orderTypeId.currentValue)
        );
        this.formSelect.controls['select'].setValue({
          value: String(orderTypeId.currentValue),
          label: orderTypeLabel?.label,
        });
      }
    }
  }

  updateShowDeployBy(showDeployBy: SimpleChange) {
    {
      if (this.showDeployBy === undefined || this.showDeployBy === null) {
        this.formSelect.controls['select'].setValue({
          value: false,
          label: 'C.Costos seleccionados',
        });
      } else {
        const showDeployByLabel = this.listShowView.itemsSelect.find(
          (x) => x.value === String(showDeployBy.currentValue)
        );
        this.formSelect.controls['select'].setValue({
          value: String(showDeployBy.currentValue),
          label: showDeployByLabel?.label,
        });
      }
    }
  }

  private handleAddRegColumn() {
    if (!this.headers.some((h) => h.propertyName === 'reg')) {
      // Create a new column
      const regColumn = new ColumnResponseModel();
      regColumn.propertyName = 'reg';
      regColumn.label = 'Reg.';
      regColumn.inputType = 'Number';
      regColumn.align = 'Center';

      // Add it to the beginning of the headers array
      this.headers.unshift({ ...regColumn });

      // Update the original positions map
      this.originalColumnPositions.clear();
      this.headers.forEach((header, index) => {
        this.originalColumnPositions.set(header.propertyName, index);
      });
    }
  }

  public setAlign(align: string): string {
    const alignMap: { [key: string]: string } = {
      Left: 'text-start',
      Center: 'text-center',
      Right: 'text-end',
    };
    return alignMap[align] || '';
  }

  public getColumnClasses(item: any, col: any): any {
    const classes: any = {
      'frozen-column': this.isColumnFrozen(col.propertyName),
      [this.setAlign(col.align)]: true,
      [col.propertyName]: true
    };

    if (col.conditionalRules) {
      for (const rule of col.conditionalRules) {
        try {
          const fn = new Function('item', `return ${rule.expression};`);
          if (fn(item)) {
            classes[rule.cssClass] = true;
          }
        } catch (e) {
          console.warn('Error evaluando regla:', rule.expression, e);
        }
      }
    }

    return classes;
  }

  // Método para manejar el toggle de columnas congeladas
  public toggleColumnLock(header: ColumnResponseModel): void {
    const propertyName = header.propertyName;
    const frozenIndex = this.frozenColumns.indexOf(propertyName);

    if (frozenIndex > -1) {
      // Descongelar columna
      this.frozenColumns = this.frozenColumns.filter(
        (col) => col !== propertyName
      );

      // Restaurar la columna a su posición original
      this.restoreColumnToOriginalPosition(propertyName);
    } else {
      // Verificar si no excedemos el máximo de columnas congeladas
      if (this.frozenColumns.length < this.MAX_FROZEN_COLUMNS) {
        // Agregar a columnas congeladas
        this.frozenColumns = [...this.frozenColumns, propertyName];

        // Mover la columna a su nueva posición
        this.moveColumnToFrozenPosition(propertyName);
      } else {
        // Mostrar mensaje de límite alcanzado
        this.confirmationService.confirm({
          message: `Has alcanzado el límite de columnas congeladas (máximo ${this.MAX_FROZEN_COLUMNS})`,
          header: 'Límite alcanzado',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Aceptar',
          rejectVisible: false,
        });
        return;
      }
    }

    // Actualizar la tabla
    this.updateTableView();
  }

  // Nuevo método para mover una columna a su posición congelada (1, 2 o 3)
  private moveColumnToFrozenPosition(propertyName: string): void {
    // El índice de la columna en el array de columnas congeladas
    const frozenIndex = this.frozenColumns.indexOf(propertyName);

    if (frozenIndex < 0) return;

    // Encontrar la columna actual
    const currentHeader = this.headers.find(
      (h) => h.propertyName === propertyName
    );
    if (!currentHeader) return;

    // Guardar la posición original si no está guardada
    if (!this.originalColumnPositions.has(propertyName)) {
      this.originalColumnPositions.set(
        propertyName,
        this.headers.indexOf(currentHeader)
      );
    }

    // Eliminar la columna de su posición actual
    this.headers = this.headers.filter((h) => h.propertyName !== propertyName);

    // Insertar la columna en la nueva posición
    // Nota: Los índices internos son independientes de showCheckbox porque
    // solo afectan al array de headers, no al DOM
    this.headers = [
      ...this.headers.slice(0, frozenIndex),
      currentHeader,
      ...this.headers.slice(frozenIndex),
    ];
  }

  // Nuevo método para restaurar una columna a su posición original
  private restoreColumnToOriginalPosition(propertyName: string): void {
    // Obtener la posición original
    const originalPosition = this.originalColumnPositions.get(propertyName);
    if (originalPosition === undefined) return;

    // Encontrar la columna actual
    const currentHeader = this.headers.find(
      (h) => h.propertyName === propertyName
    );
    if (!currentHeader) return;

    // Eliminar la columna de su posición actual
    this.headers = this.headers.filter((h) => h.propertyName !== propertyName);

    // Insertar la columna en su posición original teniendo en cuenta la cantidad de congeladas
    // Asegurándose de que no exceda la longitud actual
    const insertPosition =
      Math.min(originalPosition, this.headers.length) +
      this.frozenColumns.length;
    this.headers = [
      ...this.headers.slice(0, insertPosition),
      currentHeader,
      ...this.headers.slice(insertPosition),
    ];
  }

  // Método para actualizar la vista de la tabla después de reordenar
  private updateTableView(): void {
    // Actualizar el DOM después de los cambios
    setTimeout(() => {
      // Aplicar estilos de columnas congeladas
      this.applyFrozenColumns();

      // Forzar actualización del DOM recreando la referencia al arreglo de datos
      this.data = { ...this.data, items: [...this.data.items] };
    }, 0);
  }

  // Modificar el método applyFrozenColumns para usar la nueva posición
  private applyFrozenColumns(): void {
    if (!this.dt) return;

    // Reiniciar todas las columnas
    this.resetFrozenColumns();

    // Para cada columna congelada, aplicar estilos en su nueva posición
    this.frozenColumns.forEach((propertyName, frozenIndex) => {
      // En este caso, el índice es simplemente frozenIndex porque reordenamos físicamente
      const columnIndex = frozenIndex;

      if (columnIndex > -1) {
        const leftPosition = this.calculateLeftPosition(frozenIndex);
        this.applyStylesToColumn(columnIndex, leftPosition, frozenIndex + 1);
      }
    });
  }

  private applyStylesToColumn(
    columnIndex: number,
    leftPosition: number,
    zIndex: number
  ): void {
    if (!this.dt?.el?.nativeElement) return;

    // Ajustar el índice de columna basado en la presencia del checkbox
    const checkboxOffset = this.showCheckbox ? 1 : 0;
    const realColumnIndex = columnIndex + checkboxOffset;

    // Seleccionar todos los elementos th y td por su posición real
    const rows = this.dt.el.nativeElement.querySelectorAll('tr');

    rows.forEach((row: HTMLElement) => {
      // Para encabezados (th)
      const headerCells = row.querySelectorAll('th');
      if (headerCells.length > realColumnIndex) {
        const cell = headerCells[realColumnIndex] as HTMLElement;
        if (cell) {
          cell.style.position = 'sticky';
          cell.style.left = `${leftPosition}px`;
          cell.style.zIndex = `${zIndex}`;
          cell.style.backgroundColor = '#f8f9fa';
          cell.style.boxShadow = '2px 0 4px rgba(0,0,0,0.1)';
        }
      }

      // Para celdas de datos (td)
      const dataCells = row.querySelectorAll('td');
      if (dataCells.length > realColumnIndex) {
        const cell = dataCells[realColumnIndex] as HTMLElement;
        if (cell) {
          cell.style.position = 'sticky';
          cell.style.left = `${leftPosition}px`;
          cell.style.zIndex = `${zIndex}`;
          cell.style.backgroundColor = '#f8f9fa';
          cell.style.boxShadow = '2px 0 4px rgba(0,0,0,0.1)';
        }
      }
    });
  }

  // Actualizar el cálculo de leftPosition para usar las posiciones reordenadas
  private calculateLeftPosition(frozenIndex: number): number {
    if (frozenIndex <= 0) return 0;

    let leftPosition = 0;

    if (!this.dt?.el?.nativeElement) return leftPosition;

    // Considerar la columna de checkbox si está presente
    const checkboxOffset = this.showCheckbox ? 1 : 0;

    // Iterar por todas las columnas congeladas anteriores a esta
    for (let i = 0; i < frozenIndex; i++) {
      // Como hemos reordenado físicamente, el índice es simplemente i
      const headerCells = this.dt.el.nativeElement.querySelectorAll('th');
      const realIndex = i + checkboxOffset;

      // La primera celda siempre será la de checkbox cuando showCheckbox=true
      const prevCell = headerCells[realIndex];

      if (prevCell) {
        leftPosition += prevCell.offsetWidth;
      } else {
        leftPosition += 150; // valor por defecto
      }
    }

    return leftPosition;
  }

  // Actualizar isColumnFrozen para usar el nuevo orden
  public isColumnFrozen(propertyName: string): boolean {
    return this.frozenColumns.includes(propertyName);
  }

  getHeaderStyle(prop: string, inputType: string): { [key: string]: string } {
    const baseStyle = this.calculateFrozenStyle(prop);
    if (prop === 'reg') {
      return { ...baseStyle, 'min-width': '50px' };
    } else if (inputType == 'Number') { // Calcula width de acuerdo a los caracteres del encabezado
      const propLegth = prop.length + 5;
      return { ...baseStyle, 'max-width': `${propLegth}ch`, 'min-width': '1rem' };
    }
    return baseStyle;
  }

  public calculateFrozenStyle(propertyName: string): any {
    if (this.isColumnFrozen(propertyName)) {
      // Calcula la posición basada en el índice o un sistema más complejo si lo necesitas
      const frozenIndex = this.frozenColumns.indexOf(propertyName);
      const baseWidth = 150; // Ancho básico de columna
      const leftPosition = frozenIndex * baseWidth;

      return {
        position: 'sticky',
        left: `${leftPosition}px`,
        'z-index': '1',
      };
    }
    return {};
  }

  private resetFrozenColumns(): void {
    if (!this.dt?.el?.nativeElement) return;

    const allCells = this.dt.el.nativeElement.querySelectorAll('th, td');
    allCells.forEach((cell: HTMLElement) => {
      // Excluir la columna de acciones y la de checkbox
      const isCheckboxColumn = cell.classList.contains('p-selection-column');
      const isActionColumn = cell.classList.contains('column-action');

      if (!isActionColumn && !isCheckboxColumn) {
        cell.style.position = '';
        cell.style.left = '';
        cell.style.zIndex = '';
        cell.style.backgroundColor = '';
        cell.style.boxShadow = '';
      }
    });
  }

  public isFrozenDisabled(propertyName: string): boolean {
    return (
      !this.isColumnFrozen(propertyName) &&
      this.frozenColumns.length >= this.MAX_FROZEN_COLUMNS
    );
  }

  public getSortIcon(field: string): string {
    if (this.previousSort?.field === field) {
      return this.previousSort?.isAscsendant === 1
        ? 'pi pi-sort-up'
        : 'pi pi-sort-down';
    }
    return 'pi pi-sort';
  }

  public toggleSort(field: string) {
    if (this.previousSort?.field === field) {
      this.previousSort.isAscsendant =
        this.previousSort.isAscsendant === 1 ? -1 : 1;
    } else {
      this.previousSort = { field, isAscsendant: 1 };
    }

    this.first = 0;
    this.paginator.changePage(0);
  }

  public removeChip(filter: { propertyName: string; valueFilter: string }) {
    const { propertyName, valueFilter } = filter;

    if (valueFilter === 'deleteAll') this.removeAllFilters();

    this.removeFilter(propertyName);
    this.recoverData();
  }

  private removeAllFilters(): void {
    this.previousFilters.forEach((filter) =>
      this.removeFilter(filter.propertyName)
    );
  }

  private removeFilter(propertyName: string) {
    this.previousFilters = this.previousFilters.filter(
      (f) => f.propertyName !== propertyName
    );
    this.onFilterChange.emit({ filters: this.previousFilters });

    const headerInfo = this.headers.find(
      (h) => h.propertyName === propertyName
    );
    const inputType = headerInfo?.inputType?.toLowerCase();

    if (inputType === 'numberrange' || inputType === 'daterange' || inputType === 'datetimerange') {
      const alterControl = this.formFilters.get(
        `${propertyName}Alter`
      ) as FormControl;
      if (alterControl) {
        alterControl.setValue(null);
      }
    }

    if (this.dt?.filters && this.dt.filters[propertyName]) {
      delete this.dt.filters[propertyName];

      // Si PrimeNG espera un objeto con `.value`, asegúrate de que `filters` tenga una estructura válida
      this.dt.filters[propertyName] = { value: null, matchMode: 'contains' };
    }

    // Forzar la actualización de la tabla
    this.data = { ...this.data, items: [...this.data.items] };

    this.formFilters.get(propertyName)?.setValue(null);
  }

  public getTotalValue(propertyName: string, index: number): string {
    if (index === 0) return 'Totales';

    const total = this.data.totals.find(
      (t: { propertyName: string }) => t.propertyName === propertyName
    );
    return total?.value || '';
  }

  public getLabelName(propertyName: string): string {
    const header = this.headers.find((h) => h.propertyName === propertyName);
    return header?.label || propertyName;
  }

  public getLockIcon(propertyName: string): string {
    return this.isColumnFrozen(propertyName) ? 'pi pi-lock' : 'pi pi-lock-open';
  }

  public exportFile() {
    this.onExport.emit();
  }

  public importFile() {
    this.onImport.emit();
  }

  public bulkAction(action: string) {
    const bulkSelected = this.selectedItems.map((item: any) =>
      parseInt(item[this.dataKey] !== undefined ? item[this.dataKey] : item.id)
    );
    this.onAction.emit({ action: action, item: this.selectedItems, selectedItems: bulkSelected });
  }

  public clearSelections(): void {
    this.selectedItems = [];
    this.isSelectedItems = false;
  }

  public getButtonStyle(checkBox: CheckBoxAction): any {
    if (!Object.values(ActionColor).includes(checkBox?.color as ActionColor))
      return {};

    return {
      'background-color': checkBox.color,
      'border-color': checkBox.color,
    };
  }

  public recoverData() {
    if (this.isFilterPersistent) {
      localStorage.setItem(
        this.filterPersistentKey,
        JSON.stringify(this.previousFilters)
      );
    }

    this.onPageChange.emit({
      page: 1,
      pageSize: this.data.pageSize,
      sortField: this.previousSort?.field,
      sortOrder: this.previousSort?.isAscsendant || 1,
    });
    this.filtersChanged = false;
    this.onReload.emit();
  }

  public changePage(event: any) {
    // Actualizar el tamaño de página si cambió
    if (this.previousPageSize !== event.rows) {
      this.previousPageSize = event.rows;
      this.data.pageSize = event.rows;
      this.first = 0;

      // Emitir evento con página reseteada
      this.onPageChange.emit({
        page: 1,
        pageSize: event.rows,
        sortField: this.previousSort?.field,
        sortOrder: this.previousSort?.isAscsendant || 1,
      });

      setTimeout(() => {
        if (this.paginator) {
          this.paginator.changePage(0);
        }
      });

      return;
    }

    // Verificar si es un clic "fantasma" después de cambiar de tab
    const expectedFirst = event.page * event.rows;
    if (Math.abs(event.first - expectedFirst) > event.rows) {
      // Forzar emisión del evento con los valores correctos
      setTimeout(() => {
        this.onPageChange.emit({
          page: event.page + 1,
          pageSize: event.rows,
          sortField: this.previousSort?.field,
          sortOrder: this.previousSort?.isAscsendant || 1,
        });
      });
      return;
    }

    // Navegación normal de página
    this.first = event.first;
    this.onPageChange.emit({
      page: event.page + 1,
      pageSize: event.rows,
      sortField: this.previousSort?.field,
      sortOrder: this.previousSort?.isAscsendant || 1,
    });
    return;
  }

  public sort(event: SortEvent) {
    const { field, order } = event;
    if (
      field === this.previousSort?.field &&
      this.previousSort?.isAscsendant === event.order
    )
      return;

    this.previousSort = { field: field, isAscsendant: order };
    this.first = 0;
    this.paginator.changePage(0);
  }

  public filter(event: any) {
    if (!event) return;
    const isFilterExist = this.previousFilters.find(
      (f) => f.propertyName === event.propertyName
    );

    if (isFilterExist) {
      this.previousFilters = this.previousFilters.map((f) => {
        if (f.propertyName === event.propertyName) {
          // Para todos los tipos de filtros
          f.valueFilter = event.valueFilter;

          // Solo para rangos (fecha o numéricos)
          if ('valueAlterFilter' in event) {
            f.valueAlterFilter = event.valueAlterFilter;
          }
        }
        return f;
      });
    } else {
      const newFilter = { ...event };
      this.previousFilters.push(newFilter);
    }
    this.data.items = [];
    this.filtersChanged = true;
    this.recoverData();

    this.onFilterChange.emit({ filters: this.previousFilters });
  }

  public executeAction(
    action: TableAction,
    item: any,
    event?: MouseEvent
  ): void {
    if (event) event.stopPropagation();
    this.onAction.emit({ action: action.action, item });
  }

  /* Ajusta el ancho de la columna al contenido al hacer doble clic en el header. */
  onHeaderDblClick(event: MouseEvent, colIndex: number) {
    const tableEl: HTMLElement = this.dt.el.nativeElement as HTMLElement;

    // todos los th visibles
    const headerRow = tableEl.querySelector('thead tr');
    if (!headerRow) return;

    const ths = headerRow.querySelectorAll<HTMLTableCellElement>('th');
    const targetTh = ths[colIndex + (this.hasCheckbox() ? 1 : 0)];
    if (!targetTh) return;

    // calcular ancho máximo: header + todas las celdas de esa columna
    let maxWidth = targetTh.scrollWidth;

    const rows = tableEl.querySelectorAll<HTMLTableRowElement>('tbody tr');
    rows.forEach(row => {
      const tds = row.querySelectorAll<HTMLTableCellElement>('td');
      const td = tds[colIndex + (this.hasCheckbox() ? 1 : 0)];
      if (td) {
        const contentWidth = td.scrollWidth;
        if (contentWidth > maxWidth) {
          maxWidth = contentWidth;
        }
      }
    });

    // aplicar ancho (sumamos un pequeño padding extra)
    const finalWidth = `${maxWidth + 20}px`;
    targetTh.style.width = finalWidth;
    targetTh.style.minWidth = finalWidth;
    targetTh.style.maxWidth = finalWidth;

    // aplicar también a todos los td de esa columna
    rows.forEach(row => {
      const tds = row.querySelectorAll<HTMLTableCellElement>('td');
      const td = tds[colIndex + (this.hasCheckbox() ? 1 : 0)];
      if (td) {
        td.style.width = finalWidth;
        td.style.minWidth = finalWidth;
        td.style.maxWidth = finalWidth;
      }
    });

    // this.isResizing = true;
    // console.log('isResizing TRUE', this.isResizing)

  }

  private hasCheckbox(): boolean {
    return !!(this as any).showCheckbox;
  }

  // llamada desde el template: (onColResize)
  handleColResize(event: any) {
    this.isResizing = true;
    // console.log('isResizing', this.isResizing)

    const th = event.element as HTMLElement;
    const colIndex = Array.from(th.parentElement!.children).indexOf(th);

    th.style.removeProperty('min-width');
    th.style.removeProperty('max-width');

    const tableEl: HTMLElement = this.dt.el.nativeElement as HTMLElement;
    const rows = tableEl.querySelectorAll<HTMLTableRowElement>('tbody tr');
    rows.forEach(row => {
      const tds = row.querySelectorAll<HTMLTableCellElement>('td');
      const td = tds[colIndex];
      if (td) {
        td.style.removeProperty('min-width');
        td.style.removeProperty('max-width');
      }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onGlobalMouseUp(evt: MouseEvent) {
    //  console.log('onGlobalMouseUp', evt, this.isResizing)
    if (this.isResizing) {
      this.isResizing = false;
      this.cleanupAllColumnMinMax();
    }
  }

  cleanupAllColumnMinMax() {
    const tableEl: HTMLElement = this.dt.el.nativeElement as HTMLElement;

    const headerRow = tableEl.querySelector('thead tr');
    if (headerRow) {
      headerRow.querySelectorAll<HTMLTableCellElement>('th').forEach(th => {
        th.style.removeProperty('min-width');
        th.style.removeProperty('max-width');
      });
    }

    // todos los td
    tableEl.querySelectorAll<HTMLTableCellElement>('td').forEach(td => {
      td.style.removeProperty('min-width');
      td.style.removeProperty('max-width');
    });
  }

  /******************************************************************************************************************** */


  public onRowDoubleClick(item: any): void {
    if (this.disableDoubleClick) {
      return;
    }

    const editAction = this.actions.find(
      (action) => action.action === 'edit' || action.action === 'editar'
    );

    if (editAction) {
      this.executeAction(editAction, item);
    } else if (!this.isMultipleSelect && this.showCheckbox && !this.radioButton) {
      this.handleSingleSelectionChange(item);
    }
  }

  public getColumnIndex(index: number): number {
    // Calculate the sequential row number across pages
    // For example: Page 2 with pageSize 20 would show numbers 21-40
    return (this.data.currentPage - 1) * this.data.pageSize + (index + 1);
  }

  public transform(inputType: string, value: string): string {
    const type = inputType?.trim().toLowerCase() || '';
    switch (type) {
      case 'date':
      case 'daterange':
        return this.datePipe.transform(value, 'dd/MM/yyyy') || value;
      case 'datetime':
        return this.datePipe.transform(value, 'dd/MM/yyyy hh:mm:ss a') || value;
      case 'decimal':
        return this.decimalPipe.transform(value, '1.2-2') || value;
      case 'datetimerange': {
        const localDate = this.dateConverterService.convertUtcToLocalDateFull(value);
        return this.datePipe.transform(localDate, 'dd/MM/yyyy hh:mm:ss a', 'local') || value;
      }
      default:
        return value;
    }
  }

  public buildImageUrl(path: string | null): string {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('data:')) {
      return path;
    }
    const baseUrl = environment.apiCervinoBackend.endsWith('/')
      ? environment.apiCervinoBackend
      : `${environment.apiCervinoBackend}/`;
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return baseUrl + cleanPath;
  }

  public getImageBlobUrl(path: string | null): SafeUrl | null {
    if (!path) return null;

    if (this.imageBlobCache.has(path)) {
      return this.imageBlobCache.get(path) || null;
    }

    if (!this.loadingImages.has(path)) {
      this.loadingImages.add(path);
      this.fetchImageBlob(path);
    }

    return null;
  }

  private async fetchImageBlob(path: string): Promise<void> {
    try {
      const fullUrl = this.buildImageUrl(path);
      const blob = await firstValueFrom(
        this.httpClient.get(fullUrl, { responseType: 'blob' })
      );
      const objectUrl = URL.createObjectURL(blob);
      const safeUrl = this.sanitizer.bypassSecurityTrustUrl(objectUrl);
      this.imageBlobCache.set(path, safeUrl);
      this.cdr.markForCheck();
    } catch (error) {
      console.error('Error al cargar la imagen con autenticación:', error);
      this.imageBlobCache.set(path, null);
    } finally {
      this.loadingImages.delete(path);
    }
  }

  public onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img) {
      img.style.display = 'none';
    }
  }

  public filterStoreConsolidated(event: any) {
    this.filtersChanged = true;
    this.recoverData();
    this.data = new PagedResponseModel<any>();
    this.storeConsolidatedEmit.emit(event.value === 'true');
  }

  public filterDifferences(event: any) {
    this.filtersChanged = true;
    this.data = new PagedResponseModel<any>();
    this.differencesEmit.emit(Number(event.value));
    this.recoverData();
  }

  public filterRemissionOrderState(event: any) {
    this.filtersChanged = true;
    this.data = new PagedResponseModel<any>();
    this.remissionOrderStateEmit.emit(Number(event.value));
    this.recoverData();
  }

  public filterOrderType(event: any) {
    this.filtersChanged = true;
    this.data = new PagedResponseModel<any>();
    this.orderTypeEmit.emit(Number(event.value));
    this.recoverData();
  }

  public filterShowDeployBy(event: any) {
    this.filtersChanged = true;
    this.recoverData();
    this.data = new PagedResponseModel<any>();
    this.showDeployByEmit.emit(event.value === 'true');
  }
}
